import { ref, update } from "firebase/database";
import { PDFDocument } from "pdf-lib";
import { enquiriesDbRef } from "./dbRefs";
import { auth, database } from "../firebase/firebaseConfig";
import { functionsBaseUrl } from "../config/runtime";
import formatWeekHours from "./formatWeekHours";

const InfoItem = ({ label, value, disabled }) => (
    <div>
        <p className="font-semibold text-[12px] text-[#9298a0] mb-[4px]">
            {label}
        </p>
        <p className={`font-semibold text-[14px] ${disabled ? "text-[#717182]" : "text-[#212326]"}`}>
            {value}
        </p>
    </div>
);

const InputField = ({
    label,
    value,
    onChange,
    type = "text",
}) => (
    <div>
        <p className="font-semibold text-[12px] text-[#9298a0] mb-[6px]">
            {label}
        </p>

        <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full h-[44px] rounded-[10px] border border-[#e4e5e7] px-[14px] font-medium text-[14px] text-[#212326] focus:outline-none focus:border-[#ff5323]"
        />
    </div>
);

const getUserType = (planKey) => {
    switch (planKey) {
        case "printShop":
            return "printShop";
        case "specialist":
            return "specialist";
        case "supplier":
            return "supplier";
        default:
            return "printShop";
    }
};
const getBusinessType = (type) => {
    switch (type) {
        case "printShop":
            return "On-Demand Print Shop";
        case "supplier":
            return "Supplier Shop";
        case "specialist":
            return "Specialist Shop";
        default:
            return "On-Demand Print Shop";
    }
};


const generateShopCode = () => {
    const random = Math.floor(1 + Math.random() * 999).toString().padStart(3, '0');
    return `SIG${random}`;
};

const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    });
};

const formatEnquiriesDate = (timestamp) => {
    if (!timestamp) return "";

    return new Date(timestamp).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const isNewEnquiry = (timestamp) => {
    if (!timestamp) return false;

    const now = Date.now();
    const diffInMs = now - timestamp;

    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    return diffInDays <= 3;
};

const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "";

    const now = Date.now();
    const diff = now - timestamp;

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return "Just now";
    if (minutes < 60) return `${minutes} min ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;

    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;

    // fallback → show date
    return new Date(timestamp).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const getTimeLimit = (range) => {
    const now = Date.now();

    switch (range) {
        case "today":
            return now - 24 * 60 * 60 * 1000;

        case "this_week":
            return now - 7 * 24 * 60 * 60 * 1000;

        case "this_month":
            return now - 30 * 24 * 60 * 60 * 1000;

        default:
            return null;
    }
};


const getPdfPageCount = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    return pdfDoc.getPageCount();
};

const enquiryMarkAsRead = async (id) => {
    try {
        await update(ref(database, `Enquiries/${id}`), {
            isRead: true,
        });
    } catch (error) {
        console.error("Error marking enquiry as read:", error);
    }
};


const sendEmail = async ({ type, payload }) => {
    const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
    const response = await fetch(`${functionsBaseUrl}/sendNewEmail`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
            type,
            payload,
        }),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
        throw new Error(data.error || "Email failed");
    }

    return data;
};


const sendJobCompletedEmail = async ({
    customerName,
    customerEmail,
    shopName,
    jobId,
}) => {
    await sendEmail({
        type: "job_completed",
        payload: {
            jobId,
        },
    });
};


const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;

        reader.readAsDataURL(file);
    });

const base64ToFile = (base64, fileName) => {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);

    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], fileName, { type: mime });
};

export {
    InfoItem,
    InputField,
    getUserType,
    getBusinessType,
    generateShopCode,
    formatDate,
    formatWeekHours,
    formatEnquiriesDate,
    isNewEnquiry,
    formatTimeAgo,
    getTimeLimit,
    getPdfPageCount,
    enquiryMarkAsRead,
    sendEmail,
    sendJobCompletedEmail,
    fileToBase64,
    base64ToFile,
}
